import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useQueryClient } from 'react-query';
import { format } from 'date-fns';

import { PaginatedWrappedComponentProps, withPagination } from 'features/app/hocs/withPagination';
import { Table } from 'features/app/components/Table';
import { TableCellDef, TableRowActions } from 'features/app/ts/Table';
import { TableItemStatus } from 'features/app/components/TableItemStatus';
import { Tabs } from 'shared/tabs';
import BaseButton from 'features/app/components/BaseButton';
import { t } from 'locales/i18n';
import { OrderReportFilters } from 'features/order-history/ts/OrderReportFilters';
import { useTranslation } from 'hooks/useTranslation';
import { ErrorUtils } from 'utils';
import { useMessage } from 'features/message/hooks/useMessage';
import { Search } from 'features/app/components/Search';
import { useStyles } from './styles';
import { PartnerUser } from 'features/users/ts/PartnerUser';
import { PartnerUsersFilters } from 'features/users/ts/PartnerUsersFilters';
import { deleteUserRequest, setIsUserActiveRequest } from 'features/users/api';
import { useRoleCheck } from 'features/user/hooks/useRoleCheck';
import { useSelector } from 'store';
import { hasRequiredRoles } from 'features/user/utils/hasRequiredRoles';
import { AddUserModal } from 'features/users/components/AddUserModal';
import { EditProfileModal } from 'features/users/components/EditProfileModal';
import { ShareBalanceModal } from 'features/users/components/ShareBalanceModal';
import { ViewBalanceModal } from 'features/users/components/ViewBalanceModal';
import { confirmDialog } from 'features/app/utils/confirmDialog';
import { IdType } from 'ts/IdType';
import { useSubscription } from 'features/user/hooks/useSubscription';

const columns = [
  { field: 'name', label: t('name', { ns: 'users' }) },
  { field: 'date_joined', label: t('dateAdded', { ns: 'users' }) },
  { field: 'role', label: t('role', { ns: 'users' }) },
  { field: 'email', label: t('email', { ns: 'users' }) },
  { field: 'status', label: t('status', { ns: 'users' }) },
];

const cells: TableCellDef[] = [
  {
    field: 'name',
  },
  {
    field: 'date_joined',
    component: ({ item }) => {
      const classes = useStyles();

      return (
        <Typography variant="h6" className={classes.tableItemDefaultStyle}>
          {format(new Date(item as string), 'dd/MM/yyyy')}
        </Typography>
      );
    },
  },
  {
    field: 'role',
  },
  {
    field: 'email',
  },
  {
    field: 'is_active',
    component: ({ item }) => (
      <TableItemStatus colorScheme={item ? 'processing' : 'failed'}>
        {item ? t('userStatus.active', { ns: 'users' }) : t('userStatus.inActive', { ns: 'users' })}
      </TableItemStatus>
    ),
  },
];

const statusFilterTabs = [
  { value: '', label: t('userRoleFilter.allUsers', { ns: 'users' }) },
  { value: 'admin', label: t('userRoleFilter.admin', { ns: 'users' }) },
  { value: 'super_admin', label: t('userRoleFilter.superAdmin', { ns: 'users' }) },
];

interface UserData {
  id: IdType;
  name: string;
}

interface OrdersListProps {}

interface MappedTableItem {
  id: IdType;
  name: string;
  role: string;
  email: string;
  is_active: boolean;
  date_joined: Date;
  unmappedItem: PartnerUser;
}

const usersList = (props: OrdersListProps) => {
  const { filters, setFilters, data, isLoading, refetch } = props as PaginatedWrappedComponentProps<
    PartnerUser,
    PartnerUsersFilters
  >;

  const { t } = useTranslation('users');
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { isSubscriptionActive, subscriptionPlan } = useSubscription();
  const { showErrorMessage, showSuccessMessage } = useMessage();
  const isSuperadmin = useRoleCheck('super_admin');
  const currentUserId = useSelector(state => state.user.user?.id);

  const [modal, setModal] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [userToEdit, setUserToEdit] = React.useState<PartnerUser>();

  const invalidatePartnerBalance = React.useCallback(() => {
    queryClient.invalidateQueries('partner-balance');
  }, [queryClient]);

  const invalidateUserBalance = React.useCallback(
    id => {
      queryClient.invalidateQueries(`partner-balance-by-user-${id}`);
    },
    [queryClient]
  );

  const handleOrderStatusChange = (_: unknown, value: string) => {
    setFilters({ ...filters, role: value as PartnerUsersFilters['role'] });
  };

  const createFilterChangeHandler = (filter: keyof OrderReportFilters) => {
    return (value: unknown) => {
      setFilters({ ...filters, [filter]: value });
    };
  };

  const mappedData: MappedTableItem[] = React.useMemo(() => {
    return (
      data?.map(user => {
        const { first_name, last_name, roles, date_joined, email, is_active, id } = user;

        return {
          name: `${first_name} ${last_name}`,
          role: roles.map(role => role.readable_name).join(', '),
          date_joined,
          email,
          is_active,
          id,
          unmappedItem: user,
        };
      }) ?? []
    );
  }, [data]);

  const rowActions: TableRowActions[] = React.useMemo(
    () => [
      {
        label: ({ is_active }: MappedTableItem) =>
          is_active ? t('usersListActions.disableAccount') : t('usersListActions.enableAccount'),
        event: async ({ is_active, id }: MappedTableItem) => {
          try {
            if (is_active && !(await confirmDialog({ title: t('userMessages.statusChangeConfirmation') }))) {
              return;
            }
            await setIsUserActiveRequest(id, !is_active);
            await refetch();
            showSuccessMessage(t('userMessages.statusChanged'));
          } catch (error) {
            const errorFromServer = error?.response?.data;
            showErrorMessage(errorFromServer?.[0] || ErrorUtils.convertError(errorFromServer));
          }
        },
        shouldHide: ({ id }: MappedTableItem) => id === currentUserId || !isSuperadmin,
      },
      {
        label: t('usersListActions.deleteAccount'),
        event: async ({ id }: MappedTableItem) => {
          try {
            if (!(await confirmDialog({ title: t('userMessages.deleteConfirmation') }))) {
              return;
            }
            await deleteUserRequest(id);
            await refetch();
            showSuccessMessage(t('userMessages.userDeleted'));
          } catch (error) {
            showErrorMessage(ErrorUtils.convertError(error));
          }
        },
        shouldHide: ({ id }: MappedTableItem) => id === currentUserId || !isSuperadmin,
      },
      {
        label: t('usersListActions.editProfile'),
        event: (item: MappedTableItem) => setUserToEdit(item.unmappedItem),
        shouldHide: () => !isSuperadmin,
      },
      {
        label: t('usersListActions.shareBalance'),
        event: ({ id, name }: UserData) => {
          setUserData({ id, name });
          setModal('share-balance');
        },
        shouldHide: ({ unmappedItem }: MappedTableItem) =>
          !isSuperadmin || hasRequiredRoles(unmappedItem.roles, [{ name: 'super_admin' }]),
      },
      {
        label: t('usersListActions.viewBalance'),
        event: ({ id, name }: UserData) => {
          setUserData({ id, name });
          setModal('view-balance');
        },
        shouldHide: ({ unmappedItem }: MappedTableItem) =>
          !isSuperadmin || hasRequiredRoles(unmappedItem.roles, [{ name: 'super_admin' }]),
      },
    ],
    [refetch, showSuccessMessage, showErrorMessage, t, isSuperadmin, currentUserId]
  );

  const handleEditProfileSubmit = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  const canAddNewUser = React.useMemo((): boolean => {
    if (!isSubscriptionActive || subscriptionPlan == 'BASIC') return false;
    if (subscriptionPlan == 'PREMIUM' && Array.isArray(data) && data.length < 10) return true;
    return subscriptionPlan == 'ENTERPRISE';
  }, [isSubscriptionActive, subscriptionPlan, data]);

  return (
    <Box className={classes.root}>
      {modal === 'add-user' && (
        <AddUserModal open={modal === 'add-user'} onClose={() => setModal(null)} onSubmit={refetch} />
      )}
      {userToEdit && (
        <EditProfileModal
          open={!!userToEdit}
          onClose={() => setUserToEdit(undefined)}
          onSubmit={handleEditProfileSubmit}
          user={userToEdit as PartnerUser}
        />
      )}
      <Box className={classes.filtersContainer}>
        <Box className={classes.filters}>
          <Box>
            <Tabs value={filters.role ?? ''} tabs={statusFilterTabs} onChange={handleOrderStatusChange} />
          </Box>
        </Box>
        <Box className={classes.searchBlock}>
          <Search
            value={filters.search}
            onChange={createFilterChangeHandler('search')}
            containerClass={classes.search}
          />
          {isSuperadmin && (
            <Box marginLeft={20}>
              <BaseButton
                disabled={!canAddNewUser}
                className={classes.addButton}
                maxWidth={251}
                onClick={() => setModal('add-user')}
              >
                {t('addNewUser')}
              </BaseButton>
            </Box>
          )}
        </Box>
      </Box>
      <Table
        columns={columns}
        cells={cells as TableCellDef[]}
        data={mappedData}
        initialLoading={isLoading}
        empty={t('usersNotFound')}
        rowsActions={rowActions}
      />

      {userData && modal === 'view-balance' && (
        <ViewBalanceModal
          primaryButtonText={t('viewBalance.primaryButton')}
          errorText={t('viewBalance.errorText')}
          title={`${userData.name} ${t('viewBalance.title')}`}
          onClose={() => setModal(null)}
          open={modal === 'view-balance'}
          id={Number(userData.id)}
        />
      )}

      {userData && modal === 'share-balance' && (
        <ShareBalanceModal
          onSubmit={() => {
            invalidatePartnerBalance();
            invalidateUserBalance(userData.id);
          }}
          onClose={() => setModal(null)}
          open={modal === 'share-balance'}
          id={Number(userData.id)}
        />
      )}
    </Box>
  );
};

export const UsersListPage = withPagination(
  {
    pageSize: 8,
    getEndpoint: '/partners/partner-users/',
    queryId: 'users-list',
  },
  usersList
);
